/**
 * A2 — Importar clientes y contactos desde Excel.
 *
 * Tres pasos en una sola pantalla: descargar la plantilla (o usar tu propio Excel),
 * subir el fichero y ver una vista previa SIN guardar nada, y confirmar declarando de
 * dónde proceden los datos. El fichero no se almacena: se sube de nuevo al confirmar.
 */
import { useCallback, useRef, useState, type DragEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { downloadFile } from '../lib/download'
import { count } from '../lib/plural'
import { useAuth } from '../contexts/AuthContext'
import ImportHistory from '../components/clients/ImportHistory'
import {
  IMPORT_ORIGIN_MIN_LENGTH,
  type ClientImportPreview,
  type ClientImportResult,
  type ImportIssue,
  type ImportMode,
  type ImportRowStatus,
} from '@toga/shared'

const MAX_BYTES = 5 * 1024 * 1024

const STATUS_LABEL: Record<ImportRowStatus, string> = {
  new: 'Nuevo',
  existing: 'Ya existe',
  duplicate: 'Repetido',
  invalid: 'Con error',
}
const STATUS_CLS: Record<ImportRowStatus, string> = {
  new: 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10',
  existing: 'border-sky-500/30 text-sky-300 bg-sky-500/10',
  duplicate: 'border-amber-500/30 text-amber-300 bg-amber-500/10',
  invalid: 'border-rose-500/30 text-rose-300 bg-rose-500/10',
}
const SHEET_LABEL: Record<ImportIssue['sheet'], string> = {
  file: 'Fichero',
  clients: 'Clientes',
  contacts: 'Contactos',
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    if (body?.error) return body.error
  }
  return err instanceof Error ? err.message : fallback
}

/** CSV de incidencias. Las celdas que empiezan por = + - @ se neutralizan (Excel las leería como fórmula). */
function issuesToCsv(issues: ImportIssue[]): string {
  const cell = (v: string | number) => {
    let s = String(v).replace(/\r?\n/g, ' ')
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
    return `"${s.replace(/"/g, '""')}"`
  }
  const lines = [['Hoja', 'Fila', 'Tipo', 'Mensaje'].map(cell).join(';')]
  for (const i of issues) {
    lines.push(
      [SHEET_LABEL[i.sheet], i.row || '', i.level === 'error' ? 'Error' : 'Aviso', i.message]
        .map(cell)
        .join(';')
    )
  }
  return `﻿${lines.join('\r\n')}`
}

function downloadIssues(issues: ImportIssue[]) {
  const blob = new Blob([issuesToCsv(issues)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'incidencias-importacion.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function IssueList({ issues, total }: { issues: ImportIssue[]; total: number }) {
  if (issues.length === 0) return null
  const errors = issues.filter((i) => i.level === 'error').length
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold t-primary">
          Incidencias ({total}
          {errors > 0 && <span className="text-rose-300"> · {errors} errores</span>})
        </h4>
        <button
          type="button"
          onClick={() => downloadIssues(issues)}
          className="px-2.5 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover inline-flex items-center gap-1.5"
        >
          <Download size={12} /> Descargar (CSV)
        </button>
      </div>
      <ul className="max-h-64 overflow-y-auto rounded-lg border border-base text-xs">
        {issues.map((i, idx) => (
          <li
            key={idx}
            className="px-3 py-1.5 flex items-start gap-2 border-b border-base/50 last:border-b-0"
          >
            {i.level === 'error' ? (
              <AlertCircle size={13} className="mt-0.5 shrink-0 text-rose-400" />
            ) : (
              <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-400" />
            )}
            <span className="t-faint whitespace-nowrap">
              {SHEET_LABEL[i.sheet]}
              {i.row > 0 ? ` · fila ${i.row}` : ''}
            </span>
            <span className="t-secondary break-words min-w-0">{i.message}</span>
          </li>
        ))}
      </ul>
      {total > issues.length && (
        <p className="t-faint text-xs">
          Se muestran las primeras {issues.length}; corrige estas y vuelve a probar para ver el
          resto.
        </p>
      )}
    </div>
  )
}

export default function ClientImportPage() {
  const auth = useAuth()
  const canImport =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [file, setFile] = useState<File | null>(null)
  const [mode, setMode] = useState<ImportMode>('skip')
  const [preview, setPreview] = useState<ClientImportPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [origin, setOrigin] = useState('')
  const [committing, setCommitting] = useState(false)
  const [result, setResult] = useState<ClientImportResult | null>(null)
  const [historyKey, setHistoryKey] = useState(0)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Para ignorar respuestas de una vista previa que ya no es la última pedida.
  const requestSeq = useRef(0)

  const runPreview = useCallback(async (f: File, m: ImportMode) => {
    const seq = ++requestSeq.current
    setPreviewing(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('mode', m)
      form.append('file', f)
      const p = await api.upload<ClientImportPreview>('/clients/import/preview', form)
      if (seq === requestSeq.current) setPreview(p)
    } catch (err) {
      if (seq === requestSeq.current) {
        setPreview(null)
        setError(errorText(err, 'No se pudo leer el fichero'))
      }
    } finally {
      if (seq === requestSeq.current) setPreviewing(false)
    }
  }, [])

  const pick = (f: File | undefined | null) => {
    if (!f) return
    setResult(null)
    setPreview(null)
    setError(null)
    if (f.size > MAX_BYTES) {
      setFile(null)
      setError('El fichero pesa más de 5 MB. Divídelo en varios.')
      return
    }
    setFile(f)
    void runPreview(f, mode)
  }

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    setDragging(false)
    pick(e.dataTransfer.files[0])
  }

  const changeMode = (m: ImportMode) => {
    setMode(m)
    if (file) void runPreview(file, m)
  }

  const reset = () => {
    requestSeq.current++
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    setOrigin('')
    setPreviewing(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const [downloading, setDownloading] = useState(false)
  const getTemplate = async () => {
    setDownloading(true)
    setError(null)
    try {
      await downloadFile('/clients/import/template', 'plantilla-importacion-clientes.xlsx')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo descargar la plantilla')
    } finally {
      setDownloading(false)
    }
  }

  const t = preview?.totals
  const changes = t ? t.clientsNew + (mode === 'fill' ? t.clientsFillable : 0) + t.contactsNew : 0
  const actionParts = t
    ? [
        t.clientsNew > 0 && count(t.clientsNew, 'cliente nuevo', 'clientes nuevos'),
        t.contactsNew > 0 && count(t.contactsNew, 'contacto', 'contactos'),
        mode === 'fill' &&
          t.clientsFillable > 0 &&
          `completar ${count(t.clientsFillable, 'cliente', 'clientes')}`,
      ].filter((p): p is string => !!p)
    : []
  const originOk = origin.trim().length >= IMPORT_ORIGIN_MIN_LENGTH
  const canCommit = !!file && !!preview && changes > 0 && originOk && !committing && !previewing

  const commit = async () => {
    if (!file || !canCommit) return
    setCommitting(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('mode', mode)
      form.append('origin', origin.trim())
      form.append('file', file)
      const r = await api.upload<ClientImportResult>('/clients/import/commit', form)
      setResult(r)
      setPreview(null)
      setFile(null)
      setOrigin('')
      if (inputRef.current) inputRef.current.value = ''
      setHistoryKey((k) => k + 1)
    } catch (err) {
      setError(errorText(err, 'No se pudo importar'))
    } finally {
      setCommitting(false)
    }
  }

  if (!canImport) {
    return (
      <div className="space-y-4 animate-slideUp">
        <Link
          to="/clients"
          className="t-muted text-sm hover:underline inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} /> Clientes
        </Link>
        <div className="surface p-6 t-secondary text-sm">
          Solo los administradores y abogados pueden importar clientes.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-slideUp max-w-4xl">
      <header className="space-y-2">
        <Link
          to="/clients"
          className="t-muted text-sm hover:underline inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} /> Clientes
        </Link>
        <h2 className="text-2xl font-serif font-semibold t-primary">
          Importar clientes y contactos
        </h2>
        <p className="t-muted text-sm">
          Sube un Excel (.xlsx) con tu cartera. Antes de guardar nada verás una vista previa, y
          podrás deshacer la importación mientras los clientes no tengan actividad.
        </p>
      </header>

      {/* 1. Plantilla */}
      <section className="surface p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold t-primary">1. Prepara el fichero</h3>
          <p className="t-muted text-xs mt-1 max-w-xl">
            Descarga la plantilla (hoja <b>Clientes</b> y hoja <b>Contactos</b>, con instrucciones)
            o usa tu propio Excel: reconocemos columnas como Nombre, Apellidos, Razón social,
            NIF/CIF, Email o Teléfono.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void getTemplate()}
          disabled={downloading}
          className="px-3 py-2 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50"
        >
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          Descargar plantilla
        </button>
      </section>

      {/* Resultado de una importación recién hecha */}
      {result && (
        <section className="surface p-5 space-y-4 border-emerald-500/30">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-400 shrink-0 mt-0.5" size={20} />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold t-primary">Importación terminada</h3>
              <p className="t-secondary text-sm mt-1">
                {count(result.batch.clientsCreated, 'cliente nuevo', 'clientes nuevos')}
                {result.batch.clientsFilled > 0 && (
                  <>, {count(result.batch.clientsFilled, 'completado', 'completados')}</>
                )}{' '}
                y {count(result.batch.contactsCreated, 'contacto', 'contactos')}.{' '}
                {result.batch.clientsSkipped > 0 && (
                  <>
                    {count(
                      result.batch.clientsSkipped,
                      'fila de clientes no se importó',
                      'filas de clientes no se importaron'
                    )}
                    .
                  </>
                )}
              </p>
            </div>
          </div>
          <IssueList issues={result.issues} total={result.issuesTotal} />
          <div className="flex flex-wrap gap-2">
            <Link
              to="/clients"
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga"
            >
              Ver clientes
            </Link>
            <button
              type="button"
              onClick={reset}
              className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
            >
              Importar otro fichero
            </button>
          </div>
        </section>
      )}

      {/* 2. Subida */}
      {!result && (
        <section className="surface p-5 space-y-4">
          <h3 className="text-sm font-semibold t-primary">2. Sube el Excel</h3>
          <label
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={[
              'block rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition',
              dragging
                ? 'border-toga-500/70 bg-toga-500/10'
                : 'border-base hover:border-toga-500/50 hover:bg-hover/40',
            ].join(' ')}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="sr-only"
              onChange={(e) => pick(e.target.files?.[0])}
            />
            {file ? (
              <span className="inline-flex items-center gap-2 t-primary text-sm">
                <FileSpreadsheet size={18} className="text-emerald-400" />
                {file.name}
                <span className="t-faint text-xs">
                  ({Math.max(1, Math.round(file.size / 1024))} KB)
                </span>
              </span>
            ) : (
              <span className="inline-flex flex-col items-center gap-1.5 t-muted text-sm">
                <Upload size={20} />
                Arrastra aquí el fichero o haz clic para elegirlo
                <span className="t-faint text-xs">.xlsx · máximo 5 MB · hasta 5.000 clientes</span>
              </span>
            )}
          </label>

          {previewing && (
            <div className="t-muted text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Leyendo el fichero…
            </div>
          )}

          {error && (
            <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-start gap-2">
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </section>
      )}

      {/* 3. Vista previa */}
      {!result && preview && t && (
        <section className="surface p-5 space-y-5">
          <h3 className="text-sm font-semibold t-primary">3. Revisa antes de importar</h3>

          {/* Columnas reconocidas */}
          <div className="space-y-2 text-xs">
            {[preview.clientsSheet, preview.contactsSheet].map(
              (m) =>
                m && (
                  <div key={m.sheet}>
                    <div className="t-secondary">
                      Hoja <b>{m.sheet}</b> · cabeceras en la fila {m.headerRow}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {m.mapped.map((c) => (
                        <span
                          key={c.header + c.label}
                          className="px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                          title={`Tu columna «${c.header}» se guarda como ${c.label}`}
                        >
                          {c.header === c.label ? c.label : `${c.header} → ${c.label}`}
                        </span>
                      ))}
                      {m.ignored.map((h) => (
                        <span
                          key={h}
                          className="px-2 py-0.5 rounded-full border border-base t-faint line-through"
                          title="No se reconoce esta columna: se ignora"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                )
            )}
            {!preview.contactsSheet && (
              <div className="t-faint">
                No hay hoja de contactos (se llamaría «Contactos»): solo se importan clientes.
              </div>
            )}
          </div>

          {/* Totales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Clientes nuevos', value: t.clientsNew, cls: 'text-emerald-300' },
              { label: 'Ya existen', value: t.clientsExisting, cls: 'text-sky-300' },
              {
                label: 'Repetidos en el fichero',
                value: t.clientsDuplicated,
                cls: 'text-amber-300',
              },
              {
                label: 'Con error (no se importan)',
                value: t.clientsInvalid,
                cls: 'text-rose-300',
              },
            ].map((k) => (
              <div key={k.label} className="rounded-lg border border-base px-3 py-2.5">
                <div className={`text-xl font-semibold ${k.cls}`}>{k.value}</div>
                <div className="t-muted text-xs">{k.label}</div>
              </div>
            ))}
          </div>
          {preview.contactsSheet && (
            <p className="t-secondary text-xs">
              Contactos: <b>{t.contactsNew}</b> nuevos
              {t.contactsSkipped > 0 && (
                <>, {t.contactsSkipped} que no se añaden (ya estaban o su cliente se salta)</>
              )}
              {t.contactsInvalid > 0 && (
                <>
                  , <span className="text-rose-300">{t.contactsInvalid} con error</span>
                </>
              )}
              .
            </p>
          )}

          {/* Modo para los que ya existen */}
          {t.clientsExisting > 0 && (
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold t-primary mb-1">
                {t.clientsExisting === 1
                  ? 'Qué hacer con el cliente que ya existe'
                  : `Qué hacer con los ${t.clientsExisting} clientes que ya existen`}
              </legend>
              {(
                [
                  ['skip', 'No tocarlos', 'Se dejan como están y no se les añaden contactos.'],
                  [
                    'fill',
                    'Completar los campos vacíos',
                    `Solo rellena lo que esté en blanco (${t.clientsFillable === 1 ? '1 puede completarse' : `${t.clientsFillable} pueden completarse`}) y añade los contactos que falten. Nunca sobrescribe un dato ya escrito.`,
                  ],
                ] as [ImportMode, string, string][]
              ).map(([value, title, hint]) => (
                <label
                  key={value}
                  className={[
                    'flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer',
                    mode === value ? 'border-toga-500/50 bg-toga-500/5' : 'border-base',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="import-mode"
                    checked={mode === value}
                    onChange={() => changeMode(value)}
                    className="mt-1"
                  />
                  <span>
                    <span className="text-sm t-primary">{title}</span>
                    <span className="block t-muted text-xs">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          {/* Muestra */}
          {preview.sample.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold t-primary">
                Primeras {preview.sample.length} filas
              </h4>
              <div className="overflow-x-auto rounded-lg border border-base">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left uppercase tracking-wider t-faint border-b border-base">
                      <th className="px-3 py-2 font-medium">Fila</th>
                      <th className="px-3 py-2 font-medium">Cliente</th>
                      <th className="px-3 py-2 font-medium">NIF / CIF</th>
                      <th className="px-3 py-2 font-medium">Email</th>
                      <th className="px-3 py-2 font-medium">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r) => (
                      <tr key={r.row} className="border-b border-base/50 last:border-b-0">
                        <td className="px-3 py-1.5 t-faint">{r.row}</td>
                        <td className="px-3 py-1.5 t-primary">{r.displayName || '—'}</td>
                        <td className="px-3 py-1.5 t-secondary font-mono">{r.nationalId ?? '—'}</td>
                        <td className="px-3 py-1.5 t-secondary">{r.email ?? '—'}</td>
                        <td className="px-3 py-1.5">
                          <span
                            className={`px-2 py-0.5 rounded-full border ${STATUS_CLS[r.status]}`}
                          >
                            {STATUS_LABEL[r.status]}
                          </span>
                          {r.status === 'existing' && r.fills.length > 0 && (
                            <span className="t-faint ml-2">
                              {mode === 'fill' ? 'Se completa: ' : 'Podría completarse: '}
                              {r.fills.join(', ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <IssueList issues={preview.issues} total={preview.issuesTotal} />

          {/* Origen + confirmar */}
          <div className="space-y-2 pt-1">
            <label htmlFor="import-origin" className="text-xs font-semibold t-primary">
              Origen de los datos <span className="text-rose-400">*</span>
            </label>
            <textarea
              id="import-origin"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Ej.: cartera de clientes del despacho, exportada de nuestro programa anterior"
              className="w-full bg-transparent border border-base rounded-lg px-3 py-2 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
            />
            <p className="t-faint text-xs">
              Queda registrado junto con la importación. Importa solo datos personales que el
              despacho pueda tratar legítimamente (relación contractual, obligación legal,
              consentimiento…). Mínimo {IMPORT_ORIGIN_MIN_LENGTH} caracteres.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void commit()}
              disabled={!canCommit}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {committing && <Loader2 size={14} className="animate-spin" />}
              {changes > 0 ? `Importar: ${actionParts.join(' · ')}` : 'No hay nada que importar'}
            </button>
            <button
              type="button"
              onClick={reset}
              className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
            >
              Cancelar
            </button>
          </div>
        </section>
      )}

      <ImportHistory refreshKey={historyKey} />
    </div>
  )
}
